import type { TCompany } from "@/api/models/company/company";
import CompanyMeService, { type ICompanyMeFilter } from "@/api/models/company/companyMe";
import CompaniesService from "@/api/models/company/company";
import { useCanAccessOfficesManagement } from "@/composables/shared/useSectionPermissions";
import type { TItemQueryComposableParams } from "@/composables/shared/filters/useGeneralFilter";
import type { MaybeRefOrGetter } from "vue";
import { computed, toValue } from "vue";
import { PermActions, ESubjects } from "@/api/models/auth/ability";
import {
  createQueryKeys,
  useGenericListQuery,
  useGenericPaginatedQuery,
  useGenericQuery,
  type TItemQueryBehaviorOptions,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";
import {
  createPathParamCrudMutations,
  type IPathParamCrudService,
} from "@/composables/shared/mutations/useCrudMutations";
import type { TMutationComposableParams } from "@/composables/shared/mutations/useGenericMutation";
import { CompanyQueryKeys } from "@/composables/models/company/useCompany";

export type TCompanyMeFilters = ICompanyMeFilter;

export type TUseCompanyDetailReturn = ReturnType<typeof useGenericQuery<TCompany, string>>;

export const CompanyMeQueryKeys = createQueryKeys(CompanyMeService.queryKey, (key) => ({
  detail: key<string>("detail"),
  list: key<ICompanyMeFilter>("list"),
  paginated: key<ICompanyMeFilter>("paginated"),
}));

const companyDetailSource = (useMe: boolean) =>
  useMe
    ? {
        queryKey: CompanyMeQueryKeys.detail(),
        fetcher: (id: string) => CompanyMeService.get({ id }),
        action: $permActions.COMPANY_ME_VIEW,
      }
    : {
        queryKey: CompanyQueryKeys.detail(),
        fetcher: (id: string) => CompaniesService.get({ id }),
        action: PermActions.READ,
      };

export const useCompanyMeDetail = (
  params: MaybeRefOrGetter<TItemQueryComposableParams<string>>,
  options: TItemQueryBehaviorOptions = {}
): TUseCompanyDetailReturn => {
  const source = companyDetailSource(true);

  return useGenericQuery<TCompany, string>({
    queryKey: source.queryKey,
    params,
    fetcher: source.fetcher,
    permission: $permSubjects.PRACTICE__COMPANY,
    action: source.action,
    ...options,
    options: {
      refetchOnMount: true,
    },
  });
};

/**
 * Fetch companies visible to the current staff via `practice/company/me`.
 */
export const useCompanyMeList = (
  filters: MaybeRefOrGetter<Partial<ICompanyMeFilter>> = {},
  enabled: MaybeRefOrGetter<boolean> = true,
  options: TItemQueryBehaviorOptions = {}
) => {
  return useGenericListQuery<TCompany, Partial<ICompanyMeFilter>>({
    queryKey: CompanyMeQueryKeys.list(),
    params: { filters, enabled },
    fetcher: (f) => CompanyMeService.getAll({}, f),
    permission: $permSubjects.PRACTICE__COMPANY,
    action: $permActions.COMPANY_ME_VIEW,
    ...options,
  });
};

const { useUpdate: useUpdateCompanyMeMutation } = createPathParamCrudMutations<
  TCompany,
  { id?: string }
>(CompanyMeService as unknown as IPathParamCrudService<TCompany, { id?: string }>, [], {
  entityName: "company-me",
  permissionSubject: ESubjects.PRACTICE__COMPANY,
  invalidates: [
    CompanyMeQueryKeys.all(),
    CompanyMeQueryKeys.list(),
    CompanyMeQueryKeys.paginated(),
    CompanyQueryKeys.list(),
    CompanyQueryKeys.paginated(),
    CompanyQueryKeys.detail(),
  ],
});

export const useCompanyDetail = (
  params: MaybeRefOrGetter<TItemQueryComposableParams<string>>,
  options: { useMe?: MaybeRefOrGetter<boolean> } & TItemQueryBehaviorOptions = {}
): TUseCompanyDetailReturn => {
  const { useMe = false, ...queryBehaviorOptions } = options;
  const source = computed(() => companyDetailSource(toValue(useMe)));

  return useGenericQuery<TCompany, string>({
    queryKey: computed(() => source.value.queryKey),
    params,
    fetcher: (id) => source.value.fetcher(id),
    permission: $permSubjects.PRACTICE__COMPANY,
    action: computed(() => source.value.action),
    ...queryBehaviorOptions,
    options: {
      refetchOnMount: true,
    },
  });
};

export const useCompanyMePaginated = (params: TOwnedFilterParams<ICompanyMeFilter> = {}) => {
  return useGenericPaginatedQuery<TCompany, ICompanyMeFilter>({
    queryKey: CompanyMeQueryKeys.paginated(),
    params,
    fetcher: async ({ filters: f, pageParam }) =>
      CompanyMeService.getPaginatedFiltered({}, f ?? {}, pageParam),
    permission: $permSubjects.PRACTICE__COMPANY,
    action: $permActions.COMPANY_ME_VIEW,
  });
};

export const useOfficesManagementCompanies = () => {
  const canAccess = useCanAccessOfficesManagement();

  const meQuery = useCompanyMePaginated({ enabled: canAccess });

  const companies = computed(() => meQuery.data.value?.pages.flatMap((page) => page.results) ?? []);

  return {
    companies,
    isLoading: meQuery.isLoading,
    isFetchingNextPage: meQuery.isFetchingNextPage,
    hasNextPage: meQuery.hasNextPage,
    fetchNextPage: meQuery.fetchNextPage,
  };
};

export const useUpdateCompanyMe = (params?: TMutationComposableParams<Partial<TCompany>>) =>
  useUpdateCompanyMeMutation({
    permissions: {
      [$permActions.COMPANY_ME_EDIT]: $permSubjects.PRACTICE__COMPANY,
    },
    ...params,
  });
